import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, LifeBuoy, Mail, MessageSquareText, ShieldCheck } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';

const NEXBOT_APP_URL = 'https://www.bitrix24.es/apps/app/nextcoders.nexbot/';

const COPY = {
  en: {
    badge: 'Contact developer',
    title: 'Contact the NexBot team',
    intro:
      'Use this page to contact the NexBot team. It keeps support details on nextcoders.work and gives customers a clear first-party contact point.',
    back: 'Back to NexBot',
    demo: 'Get started',
    overview: 'Product overview',
    emailTitle: 'Support email',
    emailBody: 'Use this address for technical, compliance, and product questions.',
    responseTitle: 'Response window',
    responseBody: 'Initial response is usually shared within one business day.',
    detailsTitle: 'Helpful details',
    detailsBody: 'Include your Bitrix24 account, channel, country, and the issue or use case you want to review.',
    mainCardTitle: 'Developer contact details',
    mainCardBody:
      'NexBot is maintained by Nextcoders. Reviewers and customers can use the details below to request support, implementation guidance, and approval-related clarifications.',
    emailLabel: 'Direct email',
    emailValue: 'apps@nextcoders.work',
    supportLabel: 'Company',
    supportValue: 'Nextcoders',
    hoursLabel: 'Availability',
    hoursValue: 'Business days, Latin America and US-friendly coverage',
    noteTitle: 'Support note',
    noteBody:
      'This page includes a standard HTTPS destination so customers and partner teams can always open a working contact page in any browser environment.',
    legalTitle: 'Related pages',
    privacy: 'Privacy policy',
    eula: 'EULA',
    nexbot: 'NexBot page',
  },
  es: {
    badge: 'Contactar al desarrollador',
    title: 'Contacta al equipo de NexBot',
    intro:
      'Usa esta pagina para contactar al equipo de NexBot. Mantiene los datos de soporte dentro de nextcoders.work y ofrece un punto de contacto claro desde el propio sitio.',
    back: 'Volver a NexBot',
    demo: 'Comenzar',
    overview: 'Ver producto',
    emailTitle: 'Correo de soporte',
    emailBody: 'Usa este correo para consultas tecnicas, de cumplimiento y del producto.',
    responseTitle: 'Tiempo de respuesta',
    responseBody: 'La respuesta inicial suele compartirse dentro de un dia habil.',
    detailsTitle: 'Datos utiles',
    detailsBody: 'Incluye tu cuenta de Bitrix24, canal, pais y el problema o caso de uso que deseas revisar.',
    mainCardTitle: 'Datos de contacto del desarrollador',
    mainCardBody:
      'NexBot es mantenido por Nextcoders. Revisores y clientes pueden usar los datos de abajo para solicitar soporte, guia de implementacion y aclaraciones relacionadas con aprobacion.',
    emailLabel: 'Correo directo',
    emailValue: 'apps@nextcoders.work',
    supportLabel: 'Empresa',
    supportValue: 'Nextcoders',
    hoursLabel: 'Disponibilidad',
    hoursValue: 'Dias habiles, cobertura amigable para Latinoamerica y Estados Unidos',
    noteTitle: 'Nota de soporte',
    noteBody:
      'Esta pagina incluye un destino HTTPS estandar para que clientes y equipos aliados siempre puedan abrir un contacto funcional desde cualquier navegador.',
    legalTitle: 'Paginas relacionadas',
    privacy: 'Politica de privacidad',
    eula: 'EULA',
    nexbot: 'Pagina de NexBot',
  },
  pt: {
    badge: 'Contactar desenvolvedor',
    title: 'Fale com a equipe do NexBot',
    intro:
      'Use esta pagina para falar com a equipe do NexBot. Ela mantem os detalhes de suporte em nextcoders.work e oferece um ponto de contato claro dentro do proprio site.',
    back: 'Voltar ao NexBot',
    demo: 'Comecar',
    overview: 'Ver produto',
    emailTitle: 'Email de suporte',
    emailBody: 'Use este email para questoes tecnicas, de compliance e do produto.',
    responseTitle: 'Tempo de resposta',
    responseBody: 'A resposta inicial normalmente chega dentro de um dia util.',
    detailsTitle: 'Detalhes uteis',
    detailsBody: 'Inclua sua conta do Bitrix24, canal, pais e o problema ou caso de uso que deseja revisar.',
    mainCardTitle: 'Detalhes de contato do desenvolvedor',
    mainCardBody:
      'O NexBot e mantido pela Nextcoders. Revisores e clientes podem usar os detalhes abaixo para solicitar suporte, orientacao de implementacao e esclarecimentos ligados a aprovacao.',
    emailLabel: 'Email direto',
    emailValue: 'apps@nextcoders.work',
    supportLabel: 'Empresa',
    supportValue: 'Nextcoders',
    hoursLabel: 'Disponibilidade',
    hoursValue: 'Dias uteis, com cobertura amigavel para America Latina e Estados Unidos',
    noteTitle: 'Nota de suporte',
    noteBody:
      'Esta pagina inclui um destino HTTPS padrao para que clientes e equipes parceiras possam sempre abrir um contato funcional em qualquer ambiente de navegador.',
    legalTitle: 'Paginas relacionadas',
    privacy: 'Politica de privacidade',
    eula: 'EULA',
    nexbot: 'Pagina do NexBot',
  },
};

const getCopy = (language) => COPY[language] || COPY.es;

const PageHeader = () => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <Link to="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders';
          }}
        />
      </Link>
      <div className="flex items-center gap-4 md:gap-6">
        <LanguageSwitcher variant="compact" />
        <Link to="/nexbot-demo" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition hidden md:block">
          Demo
        </Link>
        <Link to="/nexbot-eula" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition hidden md:block">
          EULA
        </Link>
        <Link to="/nexbot-privacidad" className="text-gray-600 hover:text-[#141414] font-medium text-sm transition hidden md:block">
          Privacidad
        </Link>
      </div>
    </div>
  </header>
);

const InfoCard = ({ icon: Icon, title, body }) => (
  <div className="bg-white border border-gray-200 rounded-3xl p-6">
    <div className="inline-flex items-center justify-center h-11 w-11 rounded-2xl border border-gray-200 bg-gray-50 text-gray-900 mb-4">
      <Icon size={18} />
    </div>
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{body}</p>
  </div>
);

const ContactRow = ({ label, value }) => (
  <div className="flex flex-col gap-1 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-sm md:text-base font-medium text-[#141414] break-all">{value}</div>
  </div>
);

const NexbotContactPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = (i18n.language || 'es').split('-')[0];
  const copy = getCopy(lang);

  return (
    <div className="min-h-screen bg-transparent">
      <PageHeader />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/nexbot'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {copy.back}
          </button>

          <section className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-sm">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-gray-50 text-gray-800 px-4 py-2 rounded-full text-sm font-semibold mb-5 border border-gray-200">
                <LifeBuoy size={16} />
                {copy.badge}
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-[#141414] leading-tight">{copy.title}</h1>
              <p className="mt-5 text-base md:text-lg text-gray-600 leading-relaxed">{copy.intro}</p>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href={NEXBOT_APP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center bg-[#141414] text-white hover:bg-gray-900 font-bold py-3 px-8 rounded-xl transition"
              >
                {copy.demo}
              </a>
              <Link
                to="/nexbot"
                className="inline-flex justify-center bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3 px-8 rounded-xl transition"
              >
                {copy.overview}
              </Link>
            </div>
          </section>

          <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <InfoCard icon={Mail} title={copy.emailTitle} body={copy.emailBody} />
            <InfoCard icon={MessageSquareText} title={copy.responseTitle} body={copy.responseBody} />
            <InfoCard icon={ShieldCheck} title={copy.detailsTitle} body={copy.detailsBody} />
          </section>

          <section className="mt-8 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8">
            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10">
              <h2 className="text-2xl font-bold text-[#141414]">{copy.mainCardTitle}</h2>
              <p className="mt-3 text-gray-600 leading-relaxed max-w-3xl">{copy.mainCardBody}</p>

              <div className="mt-8 grid gap-4">
                <ContactRow label={copy.emailLabel} value={copy.emailValue} />
                <ContactRow label={copy.supportLabel} value={copy.supportValue} />
                <ContactRow label={copy.hoursLabel} value={copy.hoursValue} />
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-8">
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-gray-50 border border-gray-200 text-gray-900">
                <ShieldCheck size={20} />
              </div>
              <h2 className="mt-5 text-xl font-bold text-[#141414]">{copy.noteTitle}</h2>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">{copy.noteBody}</p>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="text-sm font-semibold text-[#141414] mb-3">{copy.legalTitle}</div>
                <div className="flex flex-col gap-3 text-sm">
                  <Link to="/nexbot" className="text-[#141414] hover:underline">{copy.nexbot}</Link>
                  <Link to="/nexbot-eula" className="text-[#141414] hover:underline">{copy.eula}</Link>
                  <Link to="/nexbot-privacidad" className="text-[#141414] hover:underline">{copy.privacy}</Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default NexbotContactPage;
